import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import {
  getConfirmationSettingsCommon,
  saveConfirmationSettingsCommon,
} from '../../../../packages/common/api';
import Page from '../../../../packages/common/shared-ui/src/components/Page';
import CheckboxIcon from '../../../../packages/common/shared-ui/src/icons/CheckboxIcon';
import CheckboxIconChecked from '../../../../packages/common/shared-ui/src/icons/CheckboxIconChecked';
import ChevronIcon from '../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import DeleteIcon from '../../../../packages/common/shared-ui/src/icons/DeleteIcon';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';

import ConfirmationSettingsTable from './components/ConfirmationSettingsTable';
import ConfirmationSettingsTrackers from './components/ConfirmationSettingsTrackers';

// Original class: blockWrapper
const StyledBlockWrapper = styled(Box)(theme => ({
  width: '100%',
  padding: '24px 32px',
  borderRadius: '16px',
  backgroundColor: '#fff',
}));
// Original class: filterSelect
const StyledFilterSelect = styled(Autocomplete)(theme => ({
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    padding: '8px 65px 8px 16px',
    lineHeight: '20px',
    height: 'auto',
    '& .MuiAutocomplete-endAdornment': {
      '& .MuiButtonBase-root.MuiIconButton-root.MuiAutocomplete-clearIndicator':
        {
          '& .MuiSvgIcon-root': {
            width: '20px',
            height: '20px',
          },
        },
      '& .MuiSvgIcon-root': {
        width: '24px',
        height: '24px',
        position: 'static',
      },
    },
    '& .MuiAutocomplete-input': {
      fontSize: '12px',
      lineHeight: '20px',
      color: '#212346',
      padding: '0',
      height: 'auto',
    },
    '&.Mui-disabled': {
      '& .MuiAutocomplete-input': {
        '&::placeholder': {
          color: '#E4E4EF',
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#E4E4EF',
      },
      '& .MuiAutocomplete-endAdornment': {
        '& .MuiSvgIcon-root path': {
          fill: '#E4E4EF',
        },
      },
    },
  },

  '&.MuiAutocomplete-hasClearIcon': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderColor: '#3448FF',
      '&:hover': {
        borderColor: '#3448FF',
      },
    },
  },

  '& .MuiFormLabel-root.MuiInputLabel-root': {
    color: '#7174AC',
    fontSize: '14px',
    lineHeight: '20px',
    top: '-5px',
    zIndex: 99,
    '&.MuiFormLabel-filled': {
      display: 'none',
    },
  },

  '&.Mui-expanded': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderRadius: '16px 16px 0 0',
      '& .MuiOutlinedInput-notchedOutline': {
        padding: 0,
      },
    },
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '&.Mui-focused': {
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '& + .MuiAutocomplete-popper': {
    minWidth: 'auto!important',
    maxWidth: '300px!important',
    '& .MuiPaper-root.MuiAutocomplete-paper': {
      borderRadius: '0 0 16px 16px',
      border: '1px solid #E4E4EF',
      boxShadow: '0px 5px 10px -8px #A0A1AA66',
      '& .MuiAutocomplete-noOptions': {
        padding: '8px 16px',
        color: '#212346',
        fontSize: '12px',
        lineHeight: '20px',
      },
      '& .MuiAutocomplete-listbox': {
        padding: '0',
        '& li': {
          whiteSpace: 'normal',
          fontSize: '12px',
          lineHeight: '20px',
          color: '#212346',
          '&.Mui-focused': {
            backgroundColor: 'transparent',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: '#DCDFF4',
          },
        },
        '&::-webkit-scrollbar': {
          width: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#7174AC',
          borderRadius: '5px',
        },
      },
    },
  },
}));
// Original class: selectLabel
const StyledSelectLabel = styled(InputLabel)(theme => ({
  '&.MuiFormLabel-root.MuiInputLabel-root': {
    position: 'static',
    width: '250px',
    color: '#41424E',
    lineHeight: '20px',
    transform: 'none',
    display: 'flex',
    flexShrink: 0,
    marginBottom: '4px',
    paddingLeft: '16px',
    '& .MuiFormLabel-asterisk': {
      color: '#E03737',
    },
    '&.Mui-focused': {
      transform: 'none',
      left: '10px',
      top: '15px',
    },
  },
}));
// Original class: fieldAutocomplete
const StyledFieldAutocomplete = styled(Autocomplete, {
  shouldForwardProp: prop => !['multipleStyle'].includes(prop),
})(({ multipleStyle }) => ({
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    background: '#fff',
    padding: '8px 65px 8px 16px',
    lineHeight: '20px',
    height: 'auto',
    ...(multipleStyle && {
      minHeight: '36px',
      padding: '0 65px 0 0',
    }),
    '& .MuiAutocomplete-endAdornment': {
      '& .MuiButtonBase-root.MuiIconButton-root.MuiAutocomplete-clearIndicator':
        {
          '& .MuiSvgIcon-root': {
            width: '20px',
            height: '20px',
          },
        },
      '& .MuiSvgIcon-root': {
        width: '24px',
        height: '24px',
        position: 'static',
      },
    },
    '& .MuiAutocomplete-input': {
      fontSize: '12px',
      lineHeight: '20px',
      color: '#212346',
      padding: '0',
      height: 'auto',
      ...(multipleStyle && {
        marginLeft: '16px',
      }),
    },
    '&.Mui-disabled': {
      '& .MuiAutocomplete-input': {
        '&::placeholder': {
          color: '#E4E4EF',
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#E4E4EF',
      },
      '& .MuiAutocomplete-endAdornment': {
        '& .MuiSvgIcon-root path': {
          fill: '#E4E4EF',
        },
      },
    },
    '&:hover': {
      '&.MuiInputBase-root': {
        '&.MuiOutlinedInput-root': {
          backgroundColor: '#fff',
        },
      },
    },
  },
  '&.MuiAutocomplete-hasClearIcon': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderColor: '#3448FF',
      '&:hover': {
        borderColor: '#3448FF',
      },
    },
  },

  '& .MuiFormLabel-root.MuiInputLabel-root': {
    color: '#7174AC',
    fontSize: '14px',
    lineHeight: '20px',
    top: '-5px',
    zIndex: 99,
    '&.MuiFormLabel-filled': {
      display: 'none',
    },
  },

  '&.Mui-expanded': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderRadius: '16px 16px 0 0',
      '& .MuiOutlinedInput-notchedOutline': {
        padding: 0,
      },
    },
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '&.Mui-focused': {
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '& + .MuiAutocomplete-popper': {
    '& .MuiPaper-root.MuiAutocomplete-paper': {
      borderRadius: '0 0 16px 16px',
      border: '1px solid #E4E4EF',
      borderTop: 'none',
      boxShadow: '0px 5px 10px -8px #A0A1AA66',
      '& .MuiAutocomplete-noOptions': {
        padding: '8px 16px',
        color: '#212346',
        fontSize: '12px',
        lineHeight: '20px',
      },
      '& .MuiAutocomplete-listbox': {
        padding: '0',
        '& li': {
          padding: '8px 10px',
          whiteSpace: 'normal',
          fontSize: '12px',
          lineHeight: '20px',
          color: '#212346',
          '& .MuiButtonBase-root.MuiCheckbox-root': {
            padding: '2px',
            marginRight: '10px',
          },
          '&.Mui-focused': {
            backgroundColor: 'transparent',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: '#DCDFF4',
          },
        },
        '&::-webkit-scrollbar': {
          width: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#7174AC',
          borderRadius: '5px',
        },
      },
    },
  },
}));
// Original class: chip
const StyledChip = styled(Chip)(theme => ({
  '&.MuiChip-root': {
    height: '30px',
    margin: '3px 4px',
    fontSize: '12px',
    display: 'flex',
    border: '1px solid #E4E4EF',
    borderRadius: '14px',
    '& .MuiChip-label': {
      padding: '0 8px',
    },
    '& .MuiChip-deleteIcon': {
      marginRight: '5px',
      position: 'static',
      height: '10px !important',
    },
  },
}));

const ConfirmationSettings = () => {
  const getLocalString = useTranslation();
  const pageTitle = getLocalString('AdminConfirmations', 'title');
  const navigate = useNavigate();
  const [settingsFields, setSettingsFields] = useState([]);
  const [initialSettingsFields, setInitialSettingsFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveSettingsloading, setSaveSettingsloading] = useState(false);

  const handleIssueError = useCallback(
    error => {
      if (error) {
        navigate(`/errors/error-${error.status}`);
      }
    },
    [navigate],
  );

  useEffect(() => {
    let ignore = false;
    async function getConfirmationCommon() {
      const { data: settings } = await getConfirmationSettingsCommon();
      if (!ignore) {
        return settings;
      }
    }
    getConfirmationCommon()
      .then(res => {
        setLoading(false);
        setSettingsFields(res);
        setInitialSettingsFields(res);
      })
      .catch(error => handleIssueError(error.response));
    return () => {
      ignore = true;
    };
  }, []);

  const handleField = useCallback(
    (data, field, deleted = false) => {
      let updatedObject;
      const pos = settingsFields.map(e => e.fieldId).indexOf(field.fieldId);
      if (field.fieldDefinition.fieldFormat === 'list') {
        if (Array.isArray(data)) {
          updatedObject = {
            ...settingsFields[pos],
            valueId: data,
            valueName: data,
            changed: true,
          };
        } else {
          updatedObject = {
            ...settingsFields[pos],
            valueId: data.valueId,
            valueName: data.valueName,
            changed: true,
          };
        }
      }
      if (field.fieldDefinition.fieldFormat === 'bool') {
        updatedObject = {
          ...settingsFields[pos],
          valueId: data.target.checked,
          valueName: data.target.checked.toString(),
          changed: true,
        };
      }
      deleted &&
        (updatedObject = {
          ...settingsFields[pos],
          valueId: '',
          valueName: '',
          changed: true,
          deleted: true,
        });
      const newFields = [
        ...settingsFields.slice(0, pos),
        updatedObject,
        ...settingsFields.slice(pos + 1),
      ];
      setSettingsFields(newFields);
    },
    [settingsFields],
  );

  const saveConfirmationSettings = useCallback(async () => {
    setSaveSettingsloading(true);
    setInitialSettingsFields([]);
    const result = settingsFields
      .filter(field => field.changed)
      .reduce((acc, field) => {
        let value = Number(field.valueId);
        field.fieldId === 'initiatorRoles' &&
          (value = field.valueId.map(item => item.valueId));
        field.fieldId === 'viewerRoles' &&
          (value = field.valueId.map(item => item.valueId));
        field.fieldDefinition.fieldFormat === 'bool' && (value = field.valueId);
        acc[field.fieldId] = value;

        return acc;
      }, {});
    try {
      const { data: settings } = await saveConfirmationSettingsCommon(result);
      if (settings) {
        setSettingsFields(settings);
        setInitialSettingsFields(settings);
        setSaveSettingsloading(false);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      handleIssueError(error.response);
    }
  }, [handleIssueError, settingsFields]);

  const handleCancel = useCallback(() => {
    setSettingsFields(initialSettingsFields);
  }, [initialSettingsFields]);

  return (
    <>
      {loading ? (
        <Grid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '70px',
          }}
        >
          <CircularProgress color='secondary' />
        </Grid>
      ) : (
        <Page title={pageTitle} width='100%'>
          <Grid
            container
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography
                variant='h1'
                sx={{
                  m: 0,
                  padding: '10px 0',
                }}
              >
                {pageTitle}
              </Typography>
            </Grid>
            <Grid>
              <Grid>
                <Button
                  variant='defaultGreyPrimary'
                  disableRipple
                  onClick={saveConfirmationSettings}
                  disabled={!settingsFields.find(field => field.changed)}
                >
                  <Typography
                    variant='h4'
                    sx={{
                      fontWeight: 700,
                    }}
                  >
                    {getLocalString('buttons', 'save')}
                  </Typography>
                </Button>
                <Button
                  onClick={handleCancel}
                  variant='defaultGreyPrimary'
                  disableRipple
                  sx={{ marginLeft: '8px' }}
                >
                  <Typography
                    variant='h4'
                    sx={{
                      fontWeight: 700,
                    }}
                  >
                    {getLocalString('buttons', 'cancel')}
                  </Typography>
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <StyledBlockWrapper
            sx={{
              padding: '0 28px 20px 28px',
              marginTop: '16px',
              position: 'relative',
            }}
          >
            {saveSettingsloading && (
              <Grid
                container
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  zIndex: 99,
                  position: 'absolute',
                }}
              >
                <CircularProgress color='secondary' />
              </Grid>
            )}
            <Grid container columnSpacing={10}>
              <Grid
                size={{
                  lg: 6,
                }}
              >
                {settingsFields
                  .filter((item, index) => index < 3)
                  .map((field, index) => (
                    <FormControl
                      required={field.fieldDefinition.isRequired}
                      key={field.fieldId}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                      }}
                    >
                      <StyledSelectLabel
                        sx={{ marginTop: '16px', width: '100%!important' }}
                      >
                        <Typography
                          variant='h5'
                          sx={{
                            color: '#41424E',
                          }}
                        >
                          {field.fieldName}
                        </Typography>
                      </StyledSelectLabel>
                      <StyledFilterSelect
                        noOptionsText={getLocalString('labels', 'notFound')}
                        disabled={field.deleted}
                        required={field.fieldDefinition.isRequired}
                        onChange={(event, newValue) =>
                          handleField(newValue, field)
                        }
                        disablePortal
                        options={field.fieldDefinition.values}
                        //sx={{ width: 253 }}
                        disableClearable
                        renderOption={(props, option) => {
                          return (
                            <MenuItem
                              {...props}
                              key={option.valueId}
                              value={option.valueId}
                              selected={
                                option.valueId.toString() === field.valueId
                              }
                            >
                              {option.valueName}
                            </MenuItem>
                          );
                        }}
                        value={
                          !field.changed &&
                          field.fieldDefinition.values.find(
                            item => item.isChecked,
                          )
                            ? field.fieldDefinition.values.find(
                                item => item.isChecked,
                              )
                            : field.valueName
                              ? {
                                  valueId: field.valueId,
                                  valueName: field.valueName,
                                }
                              : ''
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.value === value.value
                        }
                        getOptionLabel={option => option.valueName || ''}
                        renderInput={params => (
                          <TextField
                            {...params}
                            error={field.error}
                            placeholder={getLocalString('labels', 'select')}
                          />
                        )}
                        popupIcon={<ChevronIcon direction='down'></ChevronIcon>}
                        slots={{
                          popper: props => (
                            <Popper
                              {...props}
                              sx={{ maxWidth: '100%!important' }}
                              placement='bottom-start'
                            ></Popper>
                          ),
                        }}
                      />
                    </FormControl>
                  ))}
              </Grid>
              <Grid
                size={{
                  lg: 6,
                }}
              >
                {settingsFields
                  .filter((item, index) => index > 2)
                  .map((field, index) => (
                    <FormControl
                      required={field.fieldDefinition.isRequired}
                      key={field.fieldId}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                      }}
                    >
                      {field.fieldDefinition.fieldFormat !== 'bool' && (
                        <StyledSelectLabel
                          sx={{ marginTop: '16px', width: '100%!important' }}
                        >
                          <Typography
                            variant='h5'
                            sx={{
                              color: '#41424E',
                            }}
                          >
                            {field.fieldName}
                          </Typography>
                        </StyledSelectLabel>
                      )}
                      {field.fieldDefinition.isMultiple && (
                        <StyledFieldAutocomplete
                          multipleStyle={true}
                          multiple
                          disableCloseOnSelect
                          onChange={(event, newValue) =>
                            handleField(newValue, field)
                          }
                          noOptionsText={getLocalString('labels', 'notFound')}
                          options={field.fieldDefinition.values}
                          getOptionLabel={option => option.valueName || ''}
                          disableClearable
                          disablePortal
                          value={
                            !field.changed &&
                            field.fieldDefinition.values.filter(
                              item => item.isChecked,
                            ).length > 0
                              ? field.fieldDefinition.values.filter(
                                  item => item.isChecked,
                                )
                              : field.valueName
                                ? field.valueName
                                : []
                          }
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <StyledChip
                                {...getTagProps({ index })}
                                key={index}
                                deleteIcon={
                                  <DeleteIcon
                                    width={10}
                                    height={10}
                                    viewBox='0 0 10 10'
                                  />
                                }
                                variant='outlined'
                                label={option.valueName}
                              />
                            ))
                          }
                          renderOption={(props, option, { selected }) => (
                            <li {...props} key={option.valueId}>
                              <Checkbox
                                icon=<CheckboxIcon
                                  width='12'
                                  height='12'
                                  viewBox='0 0 12 12'
                                />
                                checkedIcon=<CheckboxIconChecked
                                  width='12'
                                  height='12'
                                  viewBox='0 0 12 12'
                                />
                                style={{ marginRight: 8 }}
                                checked={selected}
                              />
                              {option.valueName}
                            </li>
                          )}
                          renderInput={params => (
                            <TextField
                              {...params}
                              error={field.error}
                              placeholder={getLocalString('labels', 'select')}
                            />
                          )}
                          popupIcon={
                            <ChevronIcon direction='down'></ChevronIcon>
                          }
                          slots={{
                            popper: props => (
                              <Popper
                                {...props}
                                sx={{ maxWidth: '100%!important' }}
                                placement='bottom-start'
                              ></Popper>
                            ),
                          }}
                        />
                      )}
                      {field.fieldDefinition.fieldFormat === 'bool' && (
                        <FormControlLabel
                          sx={{ marginTop: '9px' }}
                          control={
                            <Checkbox
                              //value={Boolean(field.valueName)}
                              onChange={event => handleField(event, field)}
                              icon={
                                <CheckboxIcon
                                  width='12'
                                  height='12'
                                  viewBox='0 0 12 12'
                                />
                              }
                              checkedIcon={
                                <CheckboxIconChecked
                                  width='12'
                                  height='12'
                                  viewBox='0 0 12 12'
                                />
                              }
                              checked={field.valueName === 'true'}
                              inputProps={{ 'aria-label': 'controlled' }}
                            />
                          }
                          label={field.fieldName}
                        />
                      )}
                    </FormControl>
                  ))}
              </Grid>
            </Grid>
          </StyledBlockWrapper>
          <ConfirmationSettingsTrackers></ConfirmationSettingsTrackers>
          <ConfirmationSettingsTable></ConfirmationSettingsTable>
        </Page>
      )}
    </>
  );
};
export default ConfirmationSettings;
